import React, { useState, useEffect, useCallback } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import {
  Mail, Plus, Send, Calendar as CalIcon, Lock, AlertTriangle, Loader2,
  Users, FileText, ChevronRight, Trash2, Download, X, Eye, CheckCircle2,
  Cake, Award, BookOpen, Bell, Shield, Building, Edit3, Repeat,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '@/utils/api';

const CATEGORIA_ICONS = {
  'Políticas de la Empresa': Shield,
  'Reglas de la Empresa': Shield,
  'Beneficios': Award,
  'Cumpleaños': Cake,
  'Invitaciones a Cursos': BookOpen,
  'Recordatorios Varios': Bell,
};
const CATEGORIA_COLOR = {
  'Políticas de la Empresa': 'rose',
  'Reglas de la Empresa': 'rose',
  'Beneficios': 'emerald',
  'Cumpleaños': 'pink',
  'Invitaciones a Cursos': 'blue',
  'Recordatorios Varios': 'amber',
};
const ESTADO_COLOR = {
  borrador: 'bg-slate-100 text-slate-700',
  enviando: 'bg-blue-100 text-blue-700',
  enviado: 'bg-emerald-100 text-emerald-700',
  programado: 'bg-amber-100 text-amber-700',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es-CL', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
};

export default function ComunicacionesPersonalTab() {
  const [status, setStatus] = useState(null);
  const [comunicados, setComunicados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get('/comunicaciones/status'),
        api.get('/comunicaciones'),
      ]);
      setStatus(s.data);
      setComunicados(c.data.items || []);
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !status) {
    return <Card className="p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></Card>;
  }

  if (status && !status.autorizado) {
    return (
      <Card className="p-12 text-center border-rose-200 bg-rose-50">
        <Lock className="w-12 h-12 text-rose-400 mx-auto mb-3" />
        <h3 className="text-lg font-bold text-rose-900">Acceso restringido</h3>
        <p className="text-sm text-rose-700 mt-2">
          Este submódulo requiere el permiso <strong className="font-mono">rrhh_avanzado</strong> o ser Socio de la firma.<br />
          Solicita acceso a través del Panel de Control.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header + estado Gmail */}
      <Card className="p-4 border-slate-200">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Comunicaciones al Personal</h2>
              <p className="text-xs text-slate-500">
                Modo: <strong className={status?.gmail_conectado ? 'text-emerald-700' : 'text-amber-700'}>
                  {status?.gmail_conectado ? `Gmail real (${status.email_remitente})` : 'Mocked (logs en consola backend)'}
                </strong>
                {!status?.gmail_conectado && ' · Conecta Gmail con scope gmail.send para enviar en real'}
              </p>
            </div>
          </div>
          <Button onClick={() => setCreating(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="com-crear-btn">
            <Plus className="w-4 h-4 mr-1" /> Crear Comunicado
          </Button>
        </div>
      </Card>

      {/* Tabla */}
      <Card className="p-0 border-slate-200 overflow-hidden">
        {comunicados.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Mail className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">Aún no hay comunicaciones. Crea la primera.</p>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th className="text-left px-3 py-2">Fecha</th>
                <th className="text-left px-3 py-2">Asunto</th>
                <th className="text-left px-3 py-2">Categoría</th>
                <th className="text-left px-3 py-2">Destinatarios</th>
                <th className="text-left px-3 py-2">Estado</th>
                <th className="text-left px-3 py-2">% Lectura</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {comunicados.map(c => {
                const Icon = CATEGORIA_ICONS[c.categoria] || Mail;
                const color = CATEGORIA_COLOR[c.categoria] || 'slate';
                const isCompliance = ['Políticas de la Empresa', 'Reglas de la Empresa'].includes(c.categoria);
                return (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`com-row-${c.id}`}>
                    <td className="px-3 py-2 text-slate-600">{fmtDate(c.created_at)}</td>
                    <td className="px-3 py-2 font-semibold text-slate-800">{c.asunto}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 bg-${color}-100 text-${color}-800 rounded text-[10px]`}>
                        <Icon className="w-3 h-3" /> {c.categoria}
                      </span>
                    </td>
                    <td className="px-3 py-2 tabular-nums">{c.destinatarios?.length || 0}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${ESTADO_COLOR[c.estado] || 'bg-slate-100'}`}>
                        {c.estado}
                      </span>
                    </td>
                    <td className="px-3 py-2 tabular-nums">
                      {isCompliance ? (
                        <span className={c.compliance_pct === 100 ? 'text-emerald-700 font-bold' : (c.compliance_pct < 50 ? 'text-rose-700' : 'text-amber-700')}>
                          {c.compliance_pct ?? 0}% ({c.compliance_aceptados || 0}/{c.compliance_total || 0})
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => setDetailId(c.id)} className="p-1 text-slate-400 hover:text-indigo-600" data-testid={`com-ver-${c.id}`} title="Ver detalle">
                          <Eye className="w-4 h-4" />
                        </button>
                        {c.estado !== 'enviado' && (
                          <button
                            onClick={() => setCreating(c.id)}
                            className="p-1 text-slate-400 hover:text-amber-600"
                            data-testid={`com-edit-${c.id}`}
                            title="Editar"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm(`¿Eliminar el comunicado "${c.asunto}"?`)) return;
                            try {
                              await api.delete(`/comunicaciones/${c.id}`);
                              toast.success('Eliminado');
                              load();
                            } catch (err) { toast.error(formatErrorMessage(err)); }
                          }}
                          className="p-1 text-slate-400 hover:text-rose-600"
                          data-testid={`com-del-${c.id}`}
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {creating && (
        <CrearComunicadoDialog
          editId={creating === true ? null : creating}
          onClose={() => setCreating(false)}
          onSuccess={() => { setCreating(false); load(); }}
        />
      )}
      {detailId && (
        <DetalleComunicadoDialog
          id={detailId}
          onClose={() => setDetailId(null)}
          onChange={load}
          onEdit={(id) => { setDetailId(null); setCreating(id); }}
        />
      )}
    </div>
  );
}

// ============================================================
// CREAR / EDITAR
// ============================================================
const HORAS_DISPONIBLES = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
const PERIODICIDAD_OPCIONES = [
  { v: 'una_vez', label: 'Una sola vez' },
  { v: 'diario', label: 'Diariamente' },
  { v: 'semanal', label: 'Semanalmente' },
  { v: 'mensual', label: 'Mensualmente' },
];

const DateTimePicker = ({ value, onChange, testIdPrefix }) => {
  // value: ISO local datetime string, e.g. "2026-05-28T10:00"
  const [open, setOpen] = useState(false);
  const [tempDate, setTempDate] = useState(null);
  const [tempHora, setTempHora] = useState(10);

  useEffect(() => {
    if (value) {
      const [datePart, timePart] = value.split('T');
      if (datePart) setTempDate(new Date(datePart + 'T12:00'));
      if (timePart) setTempHora(parseInt(timePart.split(':')[0]) || 10);
    }
  }, [value]);

  const handleOk = () => {
    if (!tempDate) {
      setOpen(false);
      return;
    }
    const y = tempDate.getFullYear();
    const m = String(tempDate.getMonth() + 1).padStart(2, '0');
    const d = String(tempDate.getDate()).padStart(2, '0');
    onChange(`${y}-${m}-${d}T${String(tempHora).padStart(2, '0')}:00`);
    setOpen(false);
  };

  const handleClear = () => {
    setTempDate(null);
    onChange('');
    setOpen(false);
  };

  const formatted = value
    ? (() => {
        try {
          const dt = new Date(value);
          return dt.toLocaleDateString('es-CL', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) + ' · ' + String(dt.getHours()).padStart(2, '0') + ':00 hrs';
        } catch { return value; }
      })()
    : '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          type="button"
          className="w-full justify-start text-left font-normal"
          data-testid={`${testIdPrefix}-trigger`}
        >
          <CalIcon className="w-4 h-4 mr-2 text-slate-500" />
          {formatted || <span className="text-slate-400">Selecciona fecha y hora…</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex flex-col md:flex-row">
          <div className="border-r border-slate-200">
            <Calendar
              mode="single"
              selected={tempDate}
              onSelect={setTempDate}
              initialFocus
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
            />
          </div>
          <div className="p-3 flex flex-col gap-2 min-w-[140px]">
            <Label className="text-xs text-slate-600">Hora</Label>
            <div className="grid grid-cols-2 gap-1.5 max-h-44 overflow-y-auto">
              {HORAS_DISPONIBLES.map(h => (
                <button
                  key={h}
                  type="button"
                  onClick={() => setTempHora(h)}
                  className={`px-2 py-1.5 text-xs rounded border transition-colors ${tempHora === h ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-slate-200 hover:bg-slate-100'}`}
                  data-testid={`${testIdPrefix}-hora-${h}`}
                >
                  {String(h).padStart(2, '0')}:00
                </button>
              ))}
            </div>
            <div className="flex gap-1 mt-2">
              <Button size="sm" variant="outline" onClick={handleClear} className="flex-1 text-xs h-7">Limpiar</Button>
              <Button size="sm" onClick={handleOk} className="flex-1 text-xs h-7 bg-indigo-600 hover:bg-indigo-700" data-testid={`${testIdPrefix}-ok`}>OK</Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const CrearComunicadoDialog = ({ onClose, onSuccess, editId }) => {
  const isEdit = !!editId;
  const [catalogo, setCatalogo] = useState({ empleados: [], cargos: [], areas: [], categorias: [] });
  const [asunto, setAsunto] = useState('');
  const [categoria, setCategoria] = useState('Recordatorios Varios');
  const [cuerpoHtml, setCuerpoHtml] = useState('');
  const [segTipo, setSegTipo] = useState('todos');
  const [segAreas, setSegAreas] = useState([]);
  const [segCargos, setSegCargos] = useState([]);
  const [segEmails, setSegEmails] = useState([]);
  const [programar, setProgramar] = useState('');
  const [periodicidad, setPeriodicidad] = useState('una_vez');
  const [periodicidadFin, setPeriodicidadFin] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/comunicaciones/catalogo').then(r => setCatalogo(r.data));
    if (isEdit) {
      api.get(`/comunicaciones/${editId}`).then(r => {
        const c = r.data;
        setAsunto(c.asunto || '');
        setCategoria(c.categoria || 'Recordatorios Varios');
        setCuerpoHtml(c.cuerpo_html || '');
        const seg = c.segmentacion || {};
        setSegTipo(seg.tipo || 'todos');
        setSegAreas(seg.areas || []);
        setSegCargos(seg.cargos || []);
        setSegEmails(seg.emails || []);
        if (c.programar_para) {
          try {
            const dt = new Date(c.programar_para);
            const y = dt.getFullYear();
            const m = String(dt.getMonth() + 1).padStart(2, '0');
            const d = String(dt.getDate()).padStart(2, '0');
            setProgramar(`${y}-${m}-${d}T${String(dt.getHours()).padStart(2, '0')}:00`);
          } catch { /* ignore */ }
        }
        setPeriodicidad(c.periodicidad || 'una_vez');
        setPeriodicidadFin(c.periodicidad_fin || '');
      });
    }
  }, [editId, isEdit]);

  const toggleIn = (arr, val) => arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val];

  const previewDestinatarios = (() => {
    if (segTipo === 'todos') return catalogo.empleados.length;
    if (segTipo === 'areas') return catalogo.empleados.filter(e => (e.areas || []).some(a => segAreas.includes(a))).length;
    if (segTipo === 'cargos') return catalogo.empleados.filter(e => segCargos.includes(e.cargo)).length;
    if (segTipo === 'manual') return segEmails.length;
    return 0;
  })();

  const submit = async (enviarAhora) => {
    if (!asunto.trim() || !cuerpoHtml.trim()) {
      toast.error('Asunto y cuerpo son obligatorios');
      return;
    }
    setSubmitting(true);
    try {
      const segmentacion = { tipo: segTipo };
      if (segTipo === 'areas') segmentacion.areas = segAreas;
      if (segTipo === 'cargos') segmentacion.cargos = segCargos;
      if (segTipo === 'manual') segmentacion.emails = segEmails;
      const body = {
        asunto: asunto.trim(),
        categoria,
        cuerpo_html: cuerpoHtml,
        segmentacion,
        programar_para: !enviarAhora && programar ? new Date(programar).toISOString() : null,
        periodicidad,
        periodicidad_fin: periodicidadFin || null,
        enviar_ahora: !!enviarAhora,
      };
      if (isEdit) {
        await api.put(`/comunicaciones/${editId}`, body);
        toast.success('Comunicado actualizado');
      } else {
        await api.post('/comunicaciones', body);
        toast.success(enviarAhora ? 'Comunicado enviado' : (programar ? 'Comunicado programado' : 'Borrador guardado'));
      }
      onSuccess();
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const isCompliance = ['Políticas de la Empresa', 'Reglas de la Empresa'].includes(categoria);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="com-crear-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-indigo-600" /> {isEdit ? 'Editar Comunicado' : 'Crear Comunicado'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Asunto *</Label>
              <Input value={asunto} onChange={e => setAsunto(e.target.value)} data-testid="com-asunto" placeholder="Ej: Política de teletrabajo 2026" />
            </div>
            <div>
              <Label className="text-xs">Categoría *</Label>
              <select
                value={categoria}
                onChange={e => setCategoria(e.target.value)}
                className="w-full h-9 px-3 border border-slate-300 rounded bg-white text-sm"
                data-testid="com-categoria"
              >
                {catalogo.categorias.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {isCompliance && (
            <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-900 flex items-center gap-2">
              <Shield className="w-4 h-4 flex-shrink-0" />
              <span>Esta categoría activará el botón obligatorio <strong>"He leído y acepto"</strong> y el sistema registrará IP + timestamp por destinatario.</span>
            </div>
          )}

          <div>
            <Label className="text-xs mb-1 block">Cuerpo del comunicado *</Label>
            <p className="text-[10px] text-slate-500 mb-1">
              Tokens disponibles: <code>{'{{nombre_empleado}}'}</code>, <code>{'{{apellido_empleado}}'}</code>, <code>{'{{area_empleado}}'}</code>, <code>{'{{cargo_empleado}}'}</code>
            </p>
            <Editor
              tinymceScriptSrc={`${process.env.PUBLIC_URL || ''}/tinymce/tinymce.min.js`}
              licenseKey="gpl"
              value={cuerpoHtml}
              onEditorChange={(content) => setCuerpoHtml(content)}
              init={{
                height: 300,
                menubar: false,
                plugins: 'lists link image table code',
                toolbar: 'undo redo | bold italic underline | bullist numlist | link | alignleft aligncenter alignright | removeformat | code',
                branding: false,
                promotion: false,
                skin_url: `${process.env.PUBLIC_URL || ''}/tinymce/skins/ui/oxide`,
                content_css: `${process.env.PUBLIC_URL || ''}/tinymce/skins/content/default/content.min.css`,
              }}
            />
          </div>

          <div className="border border-slate-200 rounded p-3 bg-slate-50">
            <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
              <Users className="w-3.5 h-3.5" /> Segmentación · Destinatarios resueltos: <strong className="text-indigo-700">{previewDestinatarios}</strong>
            </p>
            <div className="flex flex-wrap gap-1 mb-2">
              {[
                { v: 'todos', label: 'Toda la empresa' },
                { v: 'areas', label: 'Por área' },
                { v: 'cargos', label: 'Por cargo' },
                { v: 'manual', label: 'Manual' },
              ].map(o => (
                <button
                  key={o.v}
                  onClick={() => setSegTipo(o.v)}
                  className={`px-3 py-1 rounded text-xs ${segTipo === o.v ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-300 hover:bg-slate-100'}`}
                  data-testid={`com-seg-${o.v}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {segTipo === 'areas' && (
              <div className="flex flex-wrap gap-1">
                {catalogo.areas.map(a => (
                  <button key={a} onClick={() => setSegAreas(toggleIn(segAreas, a))}
                    className={`px-2 py-1 text-xs rounded border ${segAreas.includes(a) ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-white border-slate-200'}`}>
                    {a}
                  </button>
                ))}
              </div>
            )}
            {segTipo === 'cargos' && (
              <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                {catalogo.cargos.map(c => (
                  <button key={c} onClick={() => setSegCargos(toggleIn(segCargos, c))}
                    className={`px-2 py-1 text-xs rounded border ${segCargos.includes(c) ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-white border-slate-200'}`}>
                    {c}
                  </button>
                ))}
              </div>
            )}
            {segTipo === 'manual' && (
              <div className="max-h-32 overflow-y-auto bg-white border border-slate-200 rounded p-2 space-y-0.5">
                {catalogo.empleados.map(e => (
                  <label key={e.email} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                    <input
                      type="checkbox"
                      checked={segEmails.includes(e.email)}
                      onChange={() => setSegEmails(toggleIn(segEmails, e.email))}
                    />
                    <span>{e.name} <span className="text-slate-400">· {e.email}</span></span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Programación + Periodicidad */}
          <div className="border border-slate-200 rounded p-3 bg-slate-50">
            <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
              <CalIcon className="w-3.5 h-3.5" /> Programación de envío
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Fecha y hora (horario laboral 9-18)</Label>
                <DateTimePicker value={programar} onChange={setProgramar} testIdPrefix="com-prog" />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1">
                  <Repeat className="w-3 h-3" /> Periodicidad
                </Label>
                <select
                  value={periodicidad}
                  onChange={e => setPeriodicidad(e.target.value)}
                  disabled={!programar}
                  className="w-full h-9 px-3 border border-slate-300 rounded bg-white text-sm disabled:bg-slate-100 disabled:text-slate-400"
                  data-testid="com-periodicidad"
                >
                  {PERIODICIDAD_OPCIONES.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
              </div>
              {periodicidad !== 'una_vez' && (
                <div>
                  <Label className="text-xs">Repetir hasta (opcional)</Label>
                  <Input
                    type="date"
                    value={periodicidadFin}
                    onChange={e => setPeriodicidadFin(e.target.value)}
                    className="h-9"
                    data-testid="com-periodicidad-fin"
                  />
                </div>
              )}
            </div>
            {programar && periodicidad !== 'una_vez' && (
              <p className="text-[10px] text-indigo-700 mt-2">
                ↻ Se enviará automáticamente {periodicidad === 'diario' ? 'cada día' : periodicidad === 'semanal' ? 'cada semana' : 'cada mes'} a la misma hora{periodicidadFin && ` hasta el ${periodicidadFin}`}.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => submit(false)} disabled={submitting} variant="outline" data-testid="com-submit-borrador">
            {programar ? <><CalIcon className="w-4 h-4 mr-1" /> {isEdit ? 'Actualizar' : 'Programar'}</> : <><FileText className="w-4 h-4 mr-1" /> {isEdit ? 'Guardar' : 'Guardar borrador'}</>}
          </Button>
          {!isEdit && (
            <Button onClick={() => submit(true)} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="com-submit-enviar">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Enviar ahora
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// DETALLE
// ============================================================
const DetalleComunicadoDialog = ({ id, onClose, onChange, onEdit }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/comunicaciones/${id}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const exportar = async () => {
    try {
      const r = await api.get(`/comunicaciones/${id}/export-compliance`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance_${id}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErrorMessage(e)); }
  };

  const enviarAhora = async () => {
    if (!window.confirm('¿Enviar este comunicado ahora?')) return;
    try {
      await api.post(`/comunicaciones/${id}/enviar`);
      toast.success('Enviado');
      load();
      onChange();
    } catch (e) { toast.error(formatErrorMessage(e)); }
  };

  const eliminar = async () => {
    if (!window.confirm('¿Eliminar este comunicado?')) return;
    try {
      await api.delete(`/comunicaciones/${id}`);
      toast.success('Eliminado');
      onClose();
      onChange();
    } catch (e) { toast.error(formatErrorMessage(e)); }
  };

  if (loading || !data) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent><Loader2 className="w-6 h-6 animate-spin mx-auto" /></DialogContent>
      </Dialog>
    );
  }

  const isCompliance = ['Políticas de la Empresa', 'Reglas de la Empresa'].includes(data.categoria);
  const total = data.destinatarios?.length || 0;
  const aceptados = data.compliance_logs?.length || 0;
  const pct = total > 0 ? Math.round((aceptados / total) * 100) : 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="com-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-indigo-600" /> {data.asunto}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded text-xs">
            <div><strong>Categoría:</strong> {data.categoria}</div>
            <div><strong>Estado:</strong> <span className={`px-2 py-0.5 rounded ${ESTADO_COLOR[data.estado] || ''}`}>{data.estado}</span></div>
            <div><strong>Creado:</strong> {fmtDate(data.created_at)}</div>
            <div><strong>Enviado:</strong> {fmtDate(data.sent_at)}</div>
            <div><strong>Destinatarios:</strong> {total}</div>
            <div><strong>Por:</strong> {data.created_by}</div>
          </div>

          {data.send_summary && (
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center">
                <p className="text-emerald-700 font-bold text-lg">{data.send_summary.enviados}</p>
                <p className="text-[10px] text-emerald-600">Enviados</p>
              </div>
              <div className="bg-rose-50 border border-rose-200 rounded p-2 text-center">
                <p className="text-rose-700 font-bold text-lg">{data.send_summary.fallidos}</p>
                <p className="text-[10px] text-rose-600">Fallidos</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded p-2 text-center">
                <p className="text-slate-700 font-bold text-lg uppercase">{data.send_summary.modo}</p>
                <p className="text-[10px] text-slate-500">Modo</p>
              </div>
            </div>
          )}

          {isCompliance && (
            <div className="border border-rose-200 bg-rose-50 rounded p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-rose-900 font-bold text-sm">
                  <Shield className="w-4 h-4" /> Compliance Laboral
                </div>
                <Button size="sm" variant="outline" onClick={exportar} data-testid="com-export-compliance">
                  <Download className="w-3.5 h-3.5 mr-1" /> Exportar Excel
                </Button>
              </div>
              <div className="mb-2">
                <div className="flex justify-between text-xs mb-1"><span>Progreso</span><span><strong>{pct}%</strong> ({aceptados}/{total})</span></div>
                <div className="w-full h-2 bg-rose-200 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
              <details className="text-xs">
                <summary className="cursor-pointer text-rose-700 font-semibold">Ver pendientes ({data.pendientes?.length || 0})</summary>
                <ul className="mt-1 max-h-32 overflow-y-auto bg-white rounded p-2">
                  {(data.pendientes || []).map(p => <li key={p.email} className="border-b border-slate-100 py-0.5">{p.name} <span className="text-slate-400">· {p.email}</span></li>)}
                </ul>
              </details>
            </div>
          )}

          <div className="border border-slate-200 rounded">
            <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-200 text-[11px] font-semibold text-slate-700">Cuerpo (preview HTML)</div>
            <div className="p-3 prose prose-sm max-w-none text-xs" dangerouslySetInnerHTML={{ __html: data.cuerpo_html }} />
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          <Button variant="outline" onClick={eliminar} className="text-rose-600 border-rose-300" data-testid="com-eliminar-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cerrar</Button>
            {data.estado !== 'enviado' && onEdit && (
              <Button variant="outline" onClick={() => onEdit(data.id)} data-testid="com-editar-btn">
                <Edit3 className="w-3.5 h-3.5 mr-1" /> Editar
              </Button>
            )}
            {data.estado !== 'enviado' && (
              <Button onClick={enviarAhora} className="bg-emerald-600 hover:bg-emerald-700">
                <Send className="w-3.5 h-3.5 mr-1" /> Enviar ahora
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
